import { ONBOARDING_BOT_CONFIGURATION_ID } from "testIds";
import styled, { css } from "styled-components";
import { StylableInput, StylablePasswordInput } from "common/Inputs/Input";
import Text from "common/Typography/Text";
import Title from "common/Typography/Title";
import { typography } from "@shared/common/Themes/typography";
import { hexToRGB } from "@shared/utils/misc";
import { newLookType } from "common/NewLook/tokens";
import ExpandingCard from "common/Boxes/ExpandingCard";
import Radio from "common/Radio/Radio";
import Checkbox from "common/Checkbox/Checkbox";
import Card from "common/Boxes/Card";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import {
  cardSurface,
  reveal,
  revealFade,
  softPanel,
  softPanelBackground,
} from "features/onboarding/common/onboardingUi";

const StepWrapper = styled.div<{ $isMobile?: boolean }>`
  max-width: ${(p) => (p.$isMobile ? 400 : 950)}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  /* Phones: the step fills the screen (OnboardingProvider), so start at the
     top rather than centring a short step (or one still loading) in it. */
  @media (max-width: 560px) {
    justify-content: flex-start;
  }
`;

const inputStyles = `
  border-radius: 8px;
`;

const StyledInputNumber = styled(CustomInputNumber).attrs({
  stringMode: true,
  hideArrows: true,
  size: "large",
})`
  ${inputStyles};
`;

const CodeInputNumber = styled(CustomInputNumber).attrs({
  width: 44,
})`
  ${inputStyles};
  text-align: center;
  height: 46px;
  &:not(:last-child) {
    margin-right: 6px;
  }
`;

const InputDescription = styled(Text).attrs({
  $size: typography.fontSizes.S,
})`
  margin-top: 4px;
  margin-bottom: 32px;
`;

const StyledInputPassword = styled(StylablePasswordInput).attrs({
  size: "large",
})`
  ${inputStyles}
`;

const StyledInput = styled(StylableInput).attrs({
  size: "large",
})`
  ${inputStyles}
`;

const InfoBackground = styled.div`
  background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.05)};
  border-radius: 8px;
  padding: 24px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${softPanel};
      padding: 24px;
    `}
`;

const BoldTitle = styled(Title).attrs({ level: 4 })`
  color: ${(p) => p.theme.colors.primaryGrey};
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  margin-bottom: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        ${newLookType.panelTitle};
        color: ${p.theme.colors.titleColor};
      }
    `}
`;

const StyledExpandingCard = styled(ExpandingCard)`
  background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.05)};
  box-shadow: none;
  margin-top: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      background: ${softPanelBackground(p.theme)};
      border-radius: ${p.theme.radii.control}px;
    `}
`;

const ExpandingCardChildrenText = styled(Text)`
  margin-right: 16px;
`;

const Logo = styled.img<{ $small: boolean }>`
  height: ${(p) => (p.$small ? 40 : 55)}px;
  width: ${(p) => (p.$small ? 40 : 55)}px;
  border-radius: ${(p) => p.theme.radii.control}px;
  margin-right: ${(p) => (p.$small ? 12 : 20)}px;
`;

const ModalTitle = styled(Title).attrs({ level: 2 })`
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  text-align: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        font-size: 18px;
        line-height: 26px;
        font-weight: 600;
        color: ${p.theme.colors.titleColor};
        text-align: start;
      }
    `}
`;

const StyledRadio = styled(Radio)`
  width: 100%;
  span.ant-radio + * {
    width: 100%;
    padding-right: 0;
  }
  &.ant-radio-wrapper {
    margin: 0;
    .ant-radio {
      align-self: baseline;
    }
  }
  .ant-radio-inner {
    margin-bottom: 2px;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox + span {
    width: 100%;
    padding-right: 0;
  }
`;

const OverviewCard = styled(Card)<{ $isMobile: boolean }>`
  padding: ${(p) => (p.$isMobile ? 16 : 24)}px;
  ${cardSurface};
  ${reveal(2)};
  /* Phones: it holds a fixed action bar, which a transform would pin to the
     card until the motion ends. */
  @media (max-width: 560px) {
    ${revealFade(2)};
    /* A native screen's list, not a card on the page: no frame, the page's
       gutter, regular-weight labels. */
    padding: 4px 0 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    /* The configuration link is a row that opens it, chevron and all. */
    [data-testid="${ONBOARDING_BOT_CONFIGURATION_ID}"] {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 52px;
      margin-top: 4px !important;
      padding: 0 !important;
      border-radius: 0;
      border-bottom: 1px solid ${(p) => p.theme.colors.cardBorderColor};
      font-size: 15px;
      font-weight: 500;
      &::after {
        content: "›";
        font-size: 22px;
        line-height: 1;
        color: ${(p) => p.theme.colors.secondaryText};
      }
    }
  }
`;

const messageStyle = css<{ $isMobile: boolean }>`
  margin-top: 4px;
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;

const ErrorText = styled(Text)<{ $isMobile: boolean }>`
  ${messageStyle};
  color: ${(p) => p.theme.colors.red};
`;

const WarningText = styled(Text)<{ $isMobile: boolean }>`
  ${messageStyle};
  color: ${(p) => p.theme.colors.gold};
`;

const OverviewTitle = styled(Title).attrs({ level: 4 })`
  color: ${(p) => p.theme.colors.primaryGrey};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin-bottom: 8px;
  /* Phones: a native list's label (regular, secondary). */
  @media (max-width: 560px) {
    && {
      font-size: 15px;
      font-weight: 400;
      color: ${(p) => p.theme.colors.secondaryText};
    }
  }
`;

const ConfirmMessage = styled(Text).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  text-align: start;
`;

export const OnboardingStyles = {
  StepWrapper,
  Input: StyledInput,
  InputNumber: StyledInputNumber,
  InputPassword: StyledInputPassword,
  InputDescription,
  CodeInputNumber,
  InfoBackground,
  BoldTitle,
  ExpandingCard: StyledExpandingCard,
  ExpandingCardChildrenText,
  Logo,
  ModalTitle,
  Radio: StyledRadio,
  Checkbox: StyledCheckbox,
  OverviewCard,
  ErrorText,
  WarningText,
  OverviewTitle,
  ConfirmMessage,
};
